import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { regenerateJoinCode, removeMember, resetStudentPassword, setStudentReadTime } from '../../lib/api';
import { TIME_SHARES } from '../../lib/reading';
import { fetchClassDecks, isDue, type Card, type ReviewDay } from '../../lib/cards';
import { dayKey, dayKeyOffset } from '../../lib/dayBoundary';
import { shortDate } from '../../lib/dates';
import type { StudentAccount } from '../../lib/types';
import { useClassroom } from '../../state/Classroom';
import ClassSettings from './ClassSettings';
import CreateClassForm from './CreateClassForm';

export default function Roster() {
  const { hydrated, active, students, members, refresh } = useClassroom();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newClass, setNewClass] = useState(() => window.location.hash === '#new-class');
  const [decks, setDecks] = useState<{ cards: Card[]; days: ReviewDay[] } | null>(null);
  const ids = students.map(s => s.user_id).join(',');
  useEffect(() => {
    if (!ids) { setDecks({ cards: [], days: [] }); return; }
    fetchClassDecks(ids.split(',')).then(setDecks).catch(() => setDecks(null));
  }, [ids]);

  if (!hydrated) return <Loading />;
  if (!active) return <div className="narrow"><h1>Roster</h1><CreateClassForm /></div>;

  const signupLink = `${window.location.origin}/?code=${active.join_code}`;
  const joinedAt = new Map(members.map(m => [m.student_id, m.joined_at]));
  const readPct = new Map(members.map(m => [m.student_id, m.read_time_pct ?? 100]));

  const copy = async () => {
    try { await navigator.clipboard.writeText(signupLink); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copy this link:', signupLink); }
  };

  const regen = async () => {
    if (!window.confirm('Make a new code? The old one stops working for new sign-ups. Students already in the class stay.')) return;
    setBusy(true); setError(null);
    try { await regenerateJoinCode(active.id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const reset = async (s: StudentAccount) => {
    const pw = window.prompt(`New password for ${s.display_name} (${s.username}) — at least 6 characters:`);
    if (!pw) return;
    if (pw.length < 6) { setError('Passwords need at least 6 characters.'); return; }
    setBusy(true); setError(null);
    try { await resetStudentPassword(s.user_id, pw); window.alert(`Done. ${s.username} can now sign in with the new password.`); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const setReadTime = async (s: StudentAccount, pct: number) => {
    setBusy(true); setError(null);
    try { await setStudentReadTime(active.id, s.user_id, pct); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const remove = async (s: StudentAccount) => {
    if (!window.confirm(`Remove ${s.display_name} from ${active.name}? Their account stays; they can rejoin with the code.`)) return;
    setBusy(true); setError(null);
    try { await removeMember(active.id, s.user_id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1>{active.name}</h1>
      <p className="sub">Students · {students.length}</p>

      <div className="panel join-panel">
        <div>
          <div className="small muted">Class code</div>
          <div className="big-code">{active.join_code}</div>
        </div>
        <div className="grow">
          <p className="small" style={{ marginTop: 0 }}>
            Students go to <strong>{window.location.host}</strong>, choose <em>New student</em>,
            and type this code. Or share the link — it fills the code in for them.
          </p>
          <div className="row">
            <button className="small" onClick={copy}>{copied ? 'Copied ✓' : 'Copy sign-up link'}</button>
            <button className="small" onClick={regen} disabled={busy}>New code</button>
          </div>
        </div>
      </div>

      {error && <div className="notice bad small" style={{ marginTop: 14 }}>{error}</div>}

      <div className="row" style={{ alignItems: 'baseline' }}>
        <h2>Students</h2>
        <span className="spacer" />
        <Link to="/roster/add" className="button small">+ Add students (Google Classroom or a list)</Link>
      </div>
      {students.length > 0 && (
        <p className="small muted" style={{ marginTop: -6 }}>
          📖 sets how much of each story’s reading time a student needs. Use it for students who read faster or slower than the class.
        </p>
      )}
      {students.length === 0 && <div className="notice">No one yet. Give your class the code above.</div>}
      <div className="list">
        {students.map(s => (
          <div key={s.user_id} className="item">
            <span className="grow">
              <Link to={`/students/${s.user_id}`} className="term">{s.display_name}</Link>
              <span className="gloss small">
                {s.username}
                {joinedAt.get(s.user_id) && ` · joined ${shortDate(joinedAt.get(s.user_id)!)}`}
              </span>
            </span>
            {decks && (
              <Link to={`/students/${s.user_id}`} className="habit-link" title="See their vocabulary">
                <DeckHabit cards={decks.cards.filter(c => c.student_id === s.user_id)}
                  days={decks.days.filter(d => d.student_id === s.user_id)} />
              </Link>
            )}
            <select className="small" style={{ width: 'auto' }} disabled={busy}
              title="How much of each story's reading time this student needs before a read counts"
              aria-label={`Story reading time for ${s.display_name}`}
              value={readPct.get(s.user_id) ?? 100} onChange={e => void setReadTime(s, Number(e.target.value))}>
              {!TIME_SHARES.some(t => t.pct === (readPct.get(s.user_id) ?? 100)) && (
                <option value={readPct.get(s.user_id)}>{readPct.get(s.user_id)}% of the time</option>
              )}
              {TIME_SHARES.map(t => <option key={t.pct} value={t.pct}>📖 {t.label}</option>)}
            </select>
            <button className="small" onClick={() => reset(s)} disabled={busy}>Reset password</button>
            <button className="small danger" onClick={() => remove(s)} disabled={busy}>Remove</button>
          </div>
        ))}
      </div>

      <h2 id="new-class">Another class</h2>
      {newClass
        ? <CreateClassForm onCreated={() => setNewClass(false)} />
        : <button onClick={() => setNewClass(true)}>+ Create another class</button>}

      <h2 id="settings">Class settings</h2>
      <ClassSettings key={active.id} c={active} />
    </div>
  );
}

/** "12 cards · 4 due · ●●○●●●● last 7 days" — whether a student reviews daily. */
function DeckHabit({ cards, days }: { cards: Card[]; days: ReviewDay[] }) {
  const reviewed = new Set(days.filter(d => d.reviewed > 0).map(d => d.day));
  const week = Array.from({ length: 7 }, (_, i) => dayKeyOffset(6 - i));
  const due = cards.filter(c => isDue(c)).length;
  return (
    <span className="habit" title="Flashcard reviews, last 7 days (today on the right)">
      <span className="small muted">{cards.length} card{cards.length === 1 ? '' : 's'}{due > 0 ? ` · ${due} due` : ''}</span>
      <span className="habit-dots">
        {week.map(d => (
          <i key={d} className={`${reviewed.has(d) ? 'on' : ''}${d === dayKey() ? ' today' : ''}`} />
        ))}
      </span>
    </span>
  );
}
